/**
 * useEmailTemplateAPI Hook
 *
 * Provides API methods for email template CRUD operations.
 * Uses cookie-based authentication (credentials: 'include').
 */

import { useState, useCallback } from 'react'
import type {
  EmailTemplateDetail,
  EmailTemplateCreate,
  EmailTemplateUpdate,
  EmailTemplateUpdateResponse,
  EmailTemplatePatch,
} from '@/lib/email-templates'

const getBackendUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  return ''
}

interface UseEmailTemplateAPIResult {
  isLoading: boolean
  error: string | null
  fetchTemplate: (templateKey: string) => Promise<EmailTemplateDetail | null>
  createTemplate: (data: EmailTemplateCreate) => Promise<EmailTemplateDetail | null>
  updateTemplate: (templateKey: string, data: EmailTemplateUpdate) => Promise<EmailTemplateUpdateResponse | null>
  patchTemplate: (templateKey: string, data: EmailTemplatePatch) => Promise<EmailTemplateDetail | null>
  deleteTemplate: (templateKey: string) => Promise<boolean>
  clearError: () => void
}

export function useEmailTemplateAPI(): UseEmailTemplateAPIResult {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const clearError = useCallback(() => setError(null), [])

  const fetchTemplate = useCallback(async (templateKey: string): Promise<EmailTemplateDetail | null> => {
    setIsLoading(true)
    setError(null)
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-templates/${templateKey}`, {
        credentials: 'include',
      })
      if (!response.ok) {
        if (response.status === 404) throw new Error('Template not found')
        throw new Error(`Failed to fetch template: ${response.status}`)
      }
      return await response.json()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch template'
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const createTemplate = useCallback(async (data: EmailTemplateCreate): Promise<EmailTemplateDetail | null> => {
    setIsLoading(true)
    setError(null)
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-templates`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 409) {
          throw new Error(errorData.detail || 'Template key already exists')
        }
        throw new Error(errorData.detail || `Failed to create template: ${response.status}`)
      }
      return await response.json()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create template'
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const updateTemplate = useCallback(async (
    templateKey: string, data: EmailTemplateUpdate
  ): Promise<EmailTemplateUpdateResponse | null> => {
    setIsLoading(true)
    setError(null)
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-templates/${templateKey}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        if (response.status === 404) throw new Error('Template not found')
        if (response.status === 400) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'Invalid update data')
        }
        if (response.status === 403) throw new Error('You do not have permission to edit templates')
        throw new Error(`Failed to update template: ${response.status}`)
      }
      return await response.json()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update template'
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const patchTemplate = useCallback(async (
    templateKey: string, data: EmailTemplatePatch
  ): Promise<EmailTemplateDetail | null> => {
    setIsLoading(true)
    setError(null)
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-templates/${templateKey}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      if (!response.ok) {
        if (response.status === 404) throw new Error('Template not found')
        throw new Error(`Failed to update template: ${response.status}`)
      }
      return await response.json()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update template'
      setError(message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const deleteTemplate = useCallback(async (templateKey: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)
    try {
      const backendUrl = getBackendUrl()
      const response = await fetch(`${backendUrl}/api/equip/email-templates/${templateKey}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!response.ok) {
        if (response.status === 404) throw new Error('Template not found')
        throw new Error(`Failed to delete template: ${response.status}`)
      }
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete template'
      setError(message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    isLoading, error,
    fetchTemplate, createTemplate, updateTemplate, patchTemplate, deleteTemplate,
    clearError,
  }
}
